import { useState, Suspense } from "react";
import "@/App.css";
import { BrowserRouter } from "react-router-dom";
import SplashScreen from "./components/splash/SplashAppleBubble";
import InstallPWAPrompt from "./components/auth/InstallPWAPrompt";
import { OfflineBanner } from "./components/common/OnlineStatusIndicator";
import { Toaster } from "sonner";
import ScrollToTop from "./components/layout/ScrollToTop";
import AppUpdateNotification from "./components/notifications/AppUpdateNotification";
import MaintenanceBanner from "./components/notifications/MaintenanceBanner";
import WelcomeAnimation from "./components/auth/WelcomeAnimation";
import BiometricSetupPrompt from "./components/auth/BiometricSetupPrompt";
import AppInstallReporter from "./components/auth/AppInstallReporter";
import ForcePasswordChangeDialog from "./components/auth/ForcePasswordChangeDialog";
import DownloadProgressOverlay from "./components/common/DownloadProgressOverlay";
import { PageLoader } from "./config/api";
import { useAuth } from "./hooks/useAuth";
import { useModuleAccess, usePublicModules } from "./hooks/useModuleAccess";
import AppRoutes from "./routes/AppRoutes";
import AIBubble from "./components/ai/AIBubble";
import NotOnLanding from "./components/landing/NotOnLanding";
import { isLandingPath } from "./components/landing/lpDom";

// Re-export API utilities for backward compatibility
// All existing imports like `import { axiosInstance } from "../App"` continue to work
export { API, axiosInstance, getCachedData, setCachedData, clearCache, getApiErrorMessage } from "./config/api";

function App() {
  const { user, loading, handleLogin, handleLogout, handleUserUpdate } = useAuth();
  const { isModuleOn } = useModuleAccess(user?.role);
  // Module kosmetik (welcome animation/confetti) berkuatkuasa SEMUA user termasuk superadmin
  const { isPublicModuleOn, loading: publicModulesLoading } = usePublicModules();
  
  const [showSplash, setShowSplash] = useState(() => {
    const lastSplashTime = localStorage.getItem('lastSplashTime');
    const now = Date.now();
    const FIVE_MINUTES = 5 * 60 * 1000;
    
    if (lastSplashTime && (now - parseInt(lastSplashTime, 10)) < FIVE_MINUTES) {
      return false;
    }
    
    const splashShownThisSession = sessionStorage.getItem('splashShown');
    if (splashShownThisSession) {
      return false;
    }
    
    const isResetPasswordPage = window.location.pathname === '/reset-password';
    const isSetupPasswordPage = window.location.pathname === '/setup-password';
    const isPublicSurveyPage = /^\/kids\d{4}$/.test(window.location.pathname);
    return !isResetPasswordPage && !isSetupPasswordPage && !isPublicSurveyPage && !isLandingPath(window.location.pathname);
  });
  
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);

  const handleSplashComplete = () => {
    localStorage.setItem('lastSplashTime', Date.now().toString());
    sessionStorage.setItem('splashShown', 'true');
    setShowSplash(false);
  };

  const onLogin = (userData, token) => {
    const mustChange = handleLogin(userData, token);
    if (mustChange) {
      setShowForcePasswordChange(true);
    }
  };

  const onPasswordChanged = (updatedUser) => {
    handleUserUpdate(updatedUser);
    setShowForcePasswordChange(false);
  };

  if (loading && !showSplash) {
    return (
      <div className="flex items-center justify-center min-h-screen relative overflow-hidden">
        <div className="absolute inset-0" style={{ backgroundImage: 'url(/bg-disneyland.jpg)', backgroundSize: 'cover', backgroundPosition: 'center top' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.3) 0%, rgba(255,240,250,0.5) 40%, rgba(255,255,255,0.65) 70%, rgba(255,255,255,0.8) 100%)' }} />
        <div className="relative z-10 text-center">
          <div className="relative inline-block">
            <div className="absolute inset-[-10px] rounded-full border-4 border-pink-200 border-t-pink-500" style={{ animation: 'spin 1.2s linear infinite' }} />
            <div style={{ animation: 'mickeyBounce 2s ease-in-out infinite' }}>
              <svg width="70" height="66" viewBox="0 0 200 180" style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.2))' }}>
                <circle cx="50" cy="38" r="38" fill="#1a1a1a"/><circle cx="150" cy="38" r="38" fill="#1a1a1a"/>
                <circle cx="100" cy="108" r="60" fill="#1a1a1a"/>
                <clipPath id="app-pants"><rect x="40" y="112" width="120" height="60"/></clipPath>
                <circle cx="100" cy="108" r="58" fill="#E31B23" clipPath="url(#app-pants)"/>
                <rect x="42" y="108" width="116" height="5" rx="2.5" fill="#1a1a1a"/>
                <ellipse cx="80" cy="138" rx="9" ry="12" fill="#FFC72C"/><ellipse cx="120" cy="138" rx="9" ry="12" fill="#FFC72C"/>
              </svg>
            </div>
          </div>
          <p className="mt-4 text-purple-700 text-sm font-bold animate-pulse" style={{ textShadow: '0 1px 4px rgba(255,255,255,0.8)' }}>Loading...</p>
          <style>{`@keyframes mickeyBounce { 0%, 100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-8px) scale(1.05); } }`}</style>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      <OfflineBanner />
      <Toaster 
        position="top-center" 
        richColors 
        toastOptions={{ style: { marginTop: '60px' } }}
      />
      <BrowserRouter>
        <ScrollToTop />
        <AppUpdateNotification user={user} />
        {user && <MaintenanceBanner user={user} />}
        <Suspense fallback={<PageLoader />}>
          <AppRoutes
            user={user}
            onLogin={onLogin}
            onLogout={handleLogout}
            onUserUpdate={handleUserUpdate}
          />
        </Suspense>
        <NotOnLanding>
        {!publicModulesLoading && isPublicModuleOn('install_pwa_prompt') && <InstallPWAPrompt />}
        <AppInstallReporter user={user} />
        {/* AI Assistant - gelembung boleh diseret (5 jenis AI ikut peranan) */}
        {user && !showForcePasswordChange && <AIBubble key={`ai-${user.id}`} user={user} />}
        {/* Tunggu status module awam siap dimuatkan dahulu - elak animasi terpapar
            seketika sebelum status ON/OFF diketahui (isu muatan pertama).
            Berkuatkuasa untuk SEMUA user termasuk superadmin. */}
        {user && !publicModulesLoading && isPublicModuleOn('welcome_animation') && <WelcomeAnimation key={`welcome-${user.id}`} user={user} showConfetti={isPublicModuleOn('welcome_confetti')} />}
        {user && !showForcePasswordChange && <BiometricSetupPrompt user={user} />}
        </NotOnLanding>
      </BrowserRouter>
      
      <ForcePasswordChangeDialog
        open={showForcePasswordChange}
        user={user}
        onPasswordChanged={onPasswordChanged}
      />
      {/* Bar kemajuan muat turun global (PDF/Excel/JSON) */}
      <DownloadProgressOverlay />
    </div>
  );
}

export default App;
